// @cogo/types-v2 — v2 schema types (LLM-native naming, stateContract-based)

// Schema types
export type {
  // element.ts — UiElement + supporting types
  ResolvedStyles,
  Layout,
  Position,
  InteractionState,
  InteractionConfig,
  TransitionConfig,
  Transform,
  TweenValue,
  TweenProps,
  ScrollProgressProps,
  AnimationConfig,
  GradientTextConfig,
  InsetShadowConfig,
  EffectsConfig,
  BackgroundLayerPosition,
  BackgroundLayer,
  DataTableColumn,
  EventRef,
  UiElement,
  UiTree,
} from "./schema/element";

export type {
  // meta.ts — BlockMeta
  WebMCPToolDef,
  WebMCPConfig,
  BlockMeta,
} from "./schema/meta";

export type {
  // events.ts — EventBinding + EventMap
  EventBinding,
  EventMap,
} from "./schema/events";

// events/ — canonical events.bindings key format + element-tree transform
export {
  bindingKey,
  parseBindingKey,
  normalizeEventBinding,
} from "./events/binding-key";
export { collectElementEvents } from "./events/element-events";
export type { ElementEventsNode } from "./events/element-events";

export type {
  // flow.ts — Flow + FlowStep variants
  Flow,
  FlowTrigger,
  FlowDuplicatePolicy,
  FlowExecutionPolicy,
  BasicStep,
  ConditionalStep,
  ExpressionStep,
  LoopStep,
  ParallelStep,
  TryCatchStep,
  SwitchStep,
  StageStep,
  SagaStep,
  FlowStep,
  FlowMap,
} from "./schema/flow";

// flow.ts — canonical flow.trigger vocabulary (value export)
export { FLOW_TRIGGERS } from "./schema/flow";

export type {
  // state.ts — BlockState
  VariableDef,
  BlockState,
} from "./schema/state";

export type {
  // data.ts — DataSource + DataMap
  DataSource,
  DataMap,
} from "./schema/data";

export type {
  // navigation.ts — Nav data contract for sidebar/nav blocks
  NavPage,
  NavCategory,
  NavTab,
  NavigationData,
  NavGuard,
} from "./schema/navigation";

export type {
  // app.ts — App, Page, Navigation
  BlockLayout,
  AbsoluteBlockPosition,
  FlowBlockPosition,
  BlockPosition,
  Navigation,
  Page,
  App,
  AppMeta,
} from "./schema/app";

export {
  isAbsolutePosition,
  isFlowPosition,
  getBlockOrder,
} from "./schema/app";

export type {
  // application-runtime-capabilities.ts — closed application WebMCP contract
  ApplicationWebMcpMode,
  ApplicationWebMcpToolRef,
  ApplicationWebMcpToolKind,
  ApplicationBlueprintRuntimeCapabilities,
  ApplicationRuntimeCapabilityProfile,
  ApplicationWebMcpToolManifestEntry,
  ApplicationWebMcpManifest,
  ApplicationRuntimeCapabilityValidation,
} from "./schema/application-runtime-capabilities";

export {
  APPLICATION_WEBMCP_MODES,
  APPLICATION_WEBMCP_CAPABILITY_UNAVAILABLE,
  APPLICATION_RUNTIME_CAPABILITIES_SCHEMA_VERSION,
  APPLICATION_WEBMCP_MANIFEST_SCHEMA_VERSION,
  isApplicationWebMcpMode,
  isApplicationApprovalPolicyRef,
  parseApplicationWebMcpToolRef,
  canonicalApplicationWebMcpToolRefs,
  validateApplicationBlueprintRuntimeCapabilities,
  validateApplicationRuntimeCapabilities,
} from "./schema/application-runtime-capabilities";

export type {
  CogoXyzPlan,
  CogoXyzWorkspace,
  CogoXyzSourceType,
  CogoXyzSourceRef,
  CogoXyzWorkItemStatus,
  CogoXyzWorkItem,
  CogoXyzBundleValidationStatus,
  CogoXyzCogoBundle,
  CogoXyzPreviewStatus,
  CogoXyzPreview,
  CogoXyzEvidenceType,
  CogoXyzEvidenceStatus,
  CogoXyzEvidenceArtifact,
  CogoXyzPermissionScope,
  CogoXyzToolRunStatus,
  CogoXyzToolRun,
  CogoXyzInputType,
  CogoXyzTarget,
  CogoXyzCreateCogoAppInput,
  CogoXyzCreateCogoAppOutput,
  CogoXyzOpenAiIngressSource,
  CogoXyzCreateOpenAiCogoAppInput,
  CogoXyzCreateOpenAiCogoAppOutput,
  CogoXyzImportHubKind,
  CogoXyzImportCogoAssetInput,
  CogoXyzImportCogoAssetOutput,
  CogoXyzUpdateCogoAppInput,
  CogoXyzUpdateCogoAppOutput,
  CogoXyzPreviewCheck,
  CogoXyzInspectPreviewInput,
  CogoXyzInspectPreviewOutput,
  CogoXyzImportFigmaFrameInput,
  CogoXyzImportFigmaFrameOutput,
  CogoXyzAttachGithubPrPreviewInput,
  CogoXyzAttachGithubPrPreviewOutput,
  CogoXyzGithubPrWriteAction,
  CogoXyzGithubPrWriteMode,
  CogoXyzPublishGithubPrPreviewInput,
  CogoXyzPublishGithubPrPreviewOutput,
  CogoXyzPreviewShareVisibility,
  CogoXyzShareCogoPreviewInput,
  CogoXyzShareCogoPreviewOutput,
  CogoXyzExportFormat,
  CogoXyzExportCogoBundleInput,
  CogoXyzExportCogoBundleOutput,
  CogoXyzDeployProvider,
  CogoXyzDeployTarget,
  CogoXyzGitProvider,
  CogoXyzGitSourceRef,
  CogoXyzCloudflareSurface,
  CogoXyzCloudflareDeploymentStatus,
  CogoXyzCloudflareDeploymentRecord,
  CogoXyzRecordCloudflareDeploymentInput,
  CogoXyzRecordCloudflareDeploymentOutput,
  CogoXyzListCloudflareDeploymentsInput,
  CogoXyzListCloudflareDeploymentsOutput,
  CogoXyzPrepareDeployInput,
  CogoXyzPrepareDeployOutput,
  CogoXyzTemplateSummary,
  CogoXyzListTemplatesInput,
  CogoXyzListTemplatesOutput,
  CogoXyzRemixTemplateInput,
  CogoXyzRemixTemplateOutput,
  CogoXyzPublishGalleryItemInput,
  CogoXyzPublishGalleryItemOutput,
  CogoXyzPreviewIdeFileInput,
  CogoXyzPreviewIdeFileOutput,
  CogoXyzProofLevel,
  CogoXyzLibraryItem,
  CogoXyzListLibraryItemsInput,
  CogoXyzListLibraryItemsOutput,
  CogoXyzGetLibraryItemInput,
  CogoXyzGetLibraryItemOutput,
  CogoXyzArchiveLibraryItemInput,
  CogoXyzArchiveLibraryItemOutput,
  CogoXyzGovernanceAuditEntry,
  CogoXyzGovernanceReport,
  CogoXyzEvidencePacket,
} from "./schema/cogo-xyz";

export type {
  // block.ts — BlockDef (combines all 6 files)
  BlockDef,
} from "./schema/block";

export type {
  // editor.ts — _editor convention types
  PlaceholderType,
} from "./schema/editor";

// Security types
export type {
  InputSymbol,
  OutputSymbol,
  StateContract,
} from "./security/state-contract";

export { SecurityError } from "./security/state-contract";

// Design tokens
export type { DesignTokenColors } from "./design-tokens";
export { DEFAULT_THEME_COLORS } from "./design-tokens";

// Render profile
export type {
  RenderLane,
  RenderProfile,
  RenderProfileSource,
} from "./render-profile";
export {
  RENDER_LANES,
  normalizeRenderLane,
  inferRenderLanesForElement,
  deriveRenderProfileFromBlocks,
} from "./render-profile";

// Agent autonomy
export type { AutonomyLevel, SeverityLevel } from "./agent/autonomy";
export { AUTONOMY_BEHAVIOR } from "./agent/autonomy";
